import {
  Bell,
  Megaphone,
  Monitor,
  Smartphone,
  KeyRound,
  Trees,
  MessageSquare,
  type LucideIcon,
} from 'lucide-react';
import type { TFunction } from 'i18next';
import type { NotificationDto, NotificationType } from '@forest/contracts';

/**
 * Render a notification's title/body in the READER's current language.
 *
 * Notifications are stored with `titleKey`/`bodyKey` + `params` plus a
 * server-rendered snapshot (`title`/`body`) in the recipient's language at
 * creation time. When the web i18n bundle has the key we re-render live via
 * `t(key, params)` so the text follows the reader's chosen language; when the
 * key is missing (unknown future type, server-only key) we fall back to the
 * stored snapshot. The snapshot is always present, so text never goes blank.
 */
export function renderNotificationText(
  n: Pick<
    NotificationDto,
    'type' | 'titleKey' | 'bodyKey' | 'params' | 'title' | 'body'
  >,
  t: TFunction,
): { title: string; body: string | null } {
  // Admin broadcasts are free text typed in a single language; their `titleKey`
  // is a `{{text}}` passthrough whose param mirrors the BODY, not the title.
  // Re-rendering would corrupt the title, so always use the stored snapshot.
  if (n.type === 'SYSTEM_BROADCAST') {
    return { title: n.title, body: n.body };
  }

  const params = (n.params ?? {}) as Record<string, unknown>;

  let title = n.title;
  if (n.titleKey && t(n.titleKey, { ns: 'notifications', defaultValue: '' })) {
    title = t(n.titleKey, { ns: 'notifications', ...params });
  }

  let body = n.body;
  if (n.bodyKey && t(n.bodyKey, { ns: 'notifications', defaultValue: '' })) {
    body = t(n.bodyKey, { ns: 'notifications', ...params });
  }

  return { title: title || n.title, body };
}

const TYPE_ICONS: Record<NotificationType, LucideIcon> = {
  SYSTEM_BROADCAST: Megaphone,
  WEB_VERSION: Monitor,
  MOBILE_VERSION: Smartphone,
  USER_PASSWORD_RESET: KeyRound,
  USER_ENTITY_UPDATED: Trees,
  PHOTO_COMMENT: MessageSquare,
};

export function notificationIcon(type: NotificationType): LucideIcon {
  return TYPE_ICONS[type] ?? Bell;
}

/**
 * Localized relative timestamp (e.g. "2 min", "3 h", "5 z"). Falls back to a
 * short absolute date for anything older than a week.
 */
export function relativeTime(iso: string, t: TFunction, locale: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.round((now - then) / 1000);

  if (diffSec < 45) return t('notifications:time.now', { defaultValue: 'acum' });
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return t('notifications:time.min', { count: diffMin, defaultValue: '{{count}} min' });
  const diffHour = Math.round(diffMin / 60);
  if (diffHour < 24) return t('notifications:time.hour', { count: diffHour, defaultValue: '{{count}} h' });
  const diffDay = Math.round(diffHour / 24);
  if (diffDay < 7) return t('notifications:time.day', { count: diffDay, defaultValue: '{{count}} z' });

  return new Date(iso).toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
  });
}
